import {
  NotifyOnChangeProps,
  useQueries,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { queryClient } from '../query-client';
import OaiApiClient from '../clients/oai-api/oai-api-client';
import {
  MarketPeriod,
  OptionContract,
  UnderlyingQuote,
} from '@oai-frontend-monorepo/types';
import { ensureMarketHours } from './useMarketHours';
import { deriveMarketPeriod } from '@oai-frontend-monorepo/utils';

type Params = {
  symbol?: string;
};

export const ensureOptionContract = async ({ symbol }: Params) => {
  return await queryClient.ensureQueryData({
    queryKey: ['quotes', symbol],
    queryFn: async () => {
      if (!symbol) {
        throw new Error('No symbols provided');
      }

      const marketInformation = await ensureMarketHours();
      const marketPeriod = deriveMarketPeriod(marketInformation);

      const res = await OaiApiClient.getQuotes([symbol]);

      const data = res?.[0];

      if (
        data.symbol === 'SPX' &&
        data.close &&
        (marketPeriod === MarketPeriod.AFTER_MARKET ||
          marketPeriod === MarketPeriod.PRE_MARKET ||
          marketPeriod === MarketPeriod.MARKET_CLOSED)
      ) {
        data.last = data.close;
      }

      queryClient.setQueryData<UnderlyingQuote | OptionContract>(
        ['quotes', symbol, true],
        data
      );

      return data;
    },
  });
};

export const prefetchOptionContract = async ({ symbol }: Params) => {
  await queryClient.prefetchQuery({
    queryKey: ['quotes', symbol],
    queryFn: async () => {
      if (!symbol) {
        throw new Error('No symbols provided');
      }

      const marketInformation = await ensureMarketHours();
      const marketPeriod = deriveMarketPeriod(marketInformation);

      const res = await OaiApiClient.getQuotes([symbol]);

      const data = res?.[0];

      if (
        data.symbol === 'SPX' &&
        data.close &&
        (marketPeriod === MarketPeriod.AFTER_MARKET ||
          marketPeriod === MarketPeriod.PRE_MARKET ||
          marketPeriod === MarketPeriod.MARKET_CLOSED)
      ) {
        data.last = data.close;
      }

      queryClient.setQueryData<UnderlyingQuote | OptionContract>(
        ['quotes', symbol, true],
        data
      );

      return data;
    },
  });
};

type Props = {
  symbols?: string[];
  enabled?: boolean;
  refetchInterval?: number;
  useArraySelector?: boolean;
  notifyOnChangeProps?: NotifyOnChangeProps;
  hasParentQuery?: boolean;
};

export const useOptionContractsForPosition = ({ symbols = [] }: Props) => {
  return useQueries({
    queries: symbols.map((s) => ({
      queryKey: ['quotes', s],
      queryFn: async () => {
        const res = (await ensureOptionContract({
          symbol: s,
        })) as OptionContract;
        return res || null;
      },
    })),
    combine: (res) => {
      const isLoading = res.some((result) => result.isLoading);

      return {
        data: !isLoading
          ? res.map((result) => result.data).filter((d) => !!d)
          : undefined,
        isLoading,
      };
    },
  });
};

type Props2<TData> = {
  symbol: string;
  enabled?: boolean;
  refetchInterval?: number;
  hasParentQuery?: boolean;
  select?: (data: UnderlyingQuote | OptionContract | null) => TData;
};

export const useOptionContract = <
  TData = UnderlyingQuote | OptionContract | null,
>({
  symbol,
  enabled = true,
  refetchInterval = 2000,
  hasParentQuery = false,
  select,
}: Props2<TData>): UseQueryResult<TData, Error> => {
  return useQuery<UnderlyingQuote | OptionContract | null, Error, TData>({
    queryKey: hasParentQuery
      ? ['quotes', symbol, hasParentQuery]
      : ['quotes', symbol],
    queryFn: async () => {
      if (hasParentQuery) {
        return (
          queryClient.getQueryData<UnderlyingQuote | OptionContract>([
            'quotes',
            symbol,
          ]) || null
        );
      }

      const marketInformation = await ensureMarketHours();
      const marketPeriod = deriveMarketPeriod(marketInformation);

      if (!symbol) {
        throw new Error('No symbols provided');
      }

      const res = await OaiApiClient.getQuotes([symbol]);

      const data = res?.[0];

      if (
        data.symbol === 'SPX' &&
        data.close &&
        (marketPeriod === MarketPeriod.AFTER_MARKET ||
          marketPeriod === MarketPeriod.PRE_MARKET ||
          marketPeriod === MarketPeriod.MARKET_CLOSED)
      ) {
        data.last = data.close;
      }

      queryClient.setQueryData<UnderlyingQuote | OptionContract>(
        ['quotes', symbol, true],
        data
      );

      return data;
    },
    enabled: !!enabled && !!symbol,
    refetchInterval,
    retry: false,
    select,
  });
};

export const useOptionContracts = ({
  symbols = [],
  enabled = true,
  refetchInterval = 2000,
  useArraySelector = false,
  notifyOnChangeProps,
  hasParentQuery,
}: Props) => {
  return useQuery({
    queryKey: hasParentQuery
      ? ['quotes', ...symbols, true]
      : ['quotes', ...symbols],
    queryFn: async () => {
      if (!symbols.length) {
        throw new Error('No symbols provided');
      }

      if (hasParentQuery) {
        const res = symbols
          .map((s) => {
            return (
              queryClient.getQueryData<UnderlyingQuote | OptionContract>([
                'quotes',
                s,
              ]) || null
            );
          })
          .filter((d) => !!d);

        return symbols.length === 1 ? res[0] : res;
      }

      const res = await OaiApiClient.getQuotes(symbols);

      const marketInformation = await ensureMarketHours();
      const marketPeriod = deriveMarketPeriod(marketInformation);

      res.forEach((item) => {
        if (
          item.symbol === 'SPX' &&
          item.close &&
          (marketPeriod === MarketPeriod.AFTER_MARKET ||
            marketPeriod === MarketPeriod.PRE_MARKET ||
            marketPeriod === MarketPeriod.MARKET_CLOSED)
        ) {
          item.last = item.close;
        }

        queryClient.setQueryData(
          ['quotes', item.symbol === '^SPX' ? 'SPX' : item.symbol],
          item
        );

        queryClient.setQueryData<UnderlyingQuote | OptionContract>(
          ['quotes', item.symbol, true],
          item
        );
      });

      return symbols.length === 1 ? res[0] : res;
    },
    select: useArraySelector
      ? (d) => {
          if (!d) {
            return undefined;
          }

          return Array.isArray(d) ? d : [d];
        }
      : undefined,
    enabled: !!enabled && !!symbols.length,
    refetchInterval,
    retry: false,
    notifyOnChangeProps,
  });
};
